import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { claimLabel } from '../../utils/claimsEnabled';

/**
 * "Claim a chapter" on the Log surface (WS5).
 *
 * Presentational: QuickLog loads suggestions and owns the claim/log calls.
 * Three still-needed chapters, or the chapter this reader already holds,
 * with a path into the in-app reader and a one-tap "Done, log it".
 */
const ClaimSlot = ({
  enabled,
  loading,
  suggestions,
  myClaim,
  claiming,
  error,
  onClaim,
  onRelease,
  onLog
}) => {
  if (!enabled) return null;

  const held = myClaim
    ? { book: myClaim.book, chapter: myClaim.chapter, chapterId: myClaim.chapterId }
    : null;

  return (
    <div className="claim-slot" data-testid="claim-slot">
      <div className="claim-slot-header">
        <h3>Claim a chapter</h3>
        <p>The community still needs these. Hold one for 24 hours.</p>
      </div>

      {error && <p className="claim-slot-error">{error}</p>}

      {loading && !held && (
        <p className="claim-slot-status">Looking for a gap…</p>
      )}

      {held && (
        <div className="claim-held" data-testid="claim-held">
          <p className="claim-held-label">
            You claimed <strong>{claimLabel(held)}</strong>. It is yours until the hold expires.
          </p>
          <div className="claim-held-actions">
            <Link
              className="claim-read-link"
              to={`/reading?chapter=${held.chapterId}`}
            >
              Read {claimLabel(held)}
            </Link>
            <button
              type="button"
              className="claim-log-button"
              onClick={() => onLog(held)}
              disabled={claiming}
            >
              Done, log it
            </button>
            <button
              type="button"
              className="claim-release-button"
              onClick={() => onRelease(myClaim)}
              disabled={claiming}
            >
              Release
            </button>
          </div>
        </div>
      )}

      {!held && !loading && suggestions.length === 0 && (
        <p className="claim-slot-status">No open gaps right now — check back after a claim expires.</p>
      )}

      {!held && suggestions.length > 0 && (
        <ul className="claim-suggestions">
          {suggestions.map(suggestion => (
            <li key={suggestion.chapterId}>
              <button
                type="button"
                className="claim-suggestion"
                onClick={() => onClaim(suggestion)}
                disabled={claiming}
              >
                <span className="claim-suggestion-ref">{claimLabel(suggestion)}</span>
                {suggestion.verseCount ? (
                  <span className="claim-suggestion-meta">{suggestion.verseCount} verses</span>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

ClaimSlot.propTypes = {
  enabled: PropTypes.bool,
  loading: PropTypes.bool,
  suggestions: PropTypes.arrayOf(PropTypes.shape({
    chapterId: PropTypes.number.isRequired,
    book: PropTypes.string.isRequired,
    chapter: PropTypes.number.isRequired,
    verseCount: PropTypes.number
  })),
  myClaim: PropTypes.shape({
    id: PropTypes.string.isRequired,
    chapterId: PropTypes.number.isRequired,
    book: PropTypes.string,
    chapter: PropTypes.number
  }),
  claiming: PropTypes.bool,
  error: PropTypes.string,
  onClaim: PropTypes.func.isRequired,
  onRelease: PropTypes.func.isRequired,
  onLog: PropTypes.func.isRequired
};

ClaimSlot.defaultProps = {
  enabled: false,
  loading: false,
  suggestions: [],
  myClaim: null,
  claiming: false,
  error: null
};

export default ClaimSlot;
